/**
 * Returns the API URL.
 */
export function apiUrl(host: string, path: string): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const origin = process.env.BACKEND_ORIGIN;
  if (origin) {
    return `${origin}${suffix}`;
  }

  // By default, use /api on same domain
  return `https://${host}/api${suffix}`;
}

/**
 * Server-side fetch to the backend for a tenant. Forwards the tenant `Host` so
 * the backend resolves the right project even when `BACKEND_ORIGIN` points at an
 * internal service. `revalidate` caches this response in Next's data cache — not
 * the rendered HTML, which stays dynamic because every page resolves its tenant
 * through `headers()`.
 *
 * `lang` is sent as BOTH `?lang=` and `Accept-Language`. The query param is the
 * one that matters twice over: the backend treats it as the highest-precedence
 * language signal, and it makes the URL — and therefore the Next data-cache key
 * — differ per locale, so a cached French response can never be served to an
 * English request.
 */
export async function backendFetch<T>(
  host: string,
  path: string,
  opts: { revalidate?: number; lang?: string } = {}
): Promise<T | null> {
  let url = apiUrl(host, path);
  const headers: Record<string, string> = { host };
  if (opts.lang) {
    url += `${url.includes("?") ? "&" : "?"}lang=${encodeURIComponent(
      opts.lang
    )}`;
    headers["accept-language"] = opts.lang;
  }

  const res = await fetch(url, {
    headers,
    next: { revalidate: opts.revalidate ?? 60 },
  });
  if (!res.ok) return null;
  return (await res.json()) as T;
}

/**
 * A backend query-string value. Arrays are joined with commas, which is how the
 * backend takes composite values (`bbox=west,south,east,north`) and plain lists
 * (`fields=a,b`). Use `repeated()` for parameters it reads as repeated instead.
 */
export type QueryValue =
  | string
  | number
  | boolean
  | (string | number)[]
  | RepeatedValue
  | null
  | undefined;

export type QueryParams = Record<string, QueryValue>;

/** A parameter the backend reads as a repeated occurrence, not a joined list. */
export interface RepeatedValue {
  repeated: string[];
}

/**
 * Send each value as its own occurrence of the parameter — `?filter=a&filter=b`.
 *
 * Explicit rather than inferred from the value being an array, because the two
 * conventions are both in use and neither is guessable: `bbox` is one composite
 * value written as four comma-separated numbers, while `filter` is a list of
 * independent clauses whose values may themselves contain commas.
 */
export function repeated(values: string[]): RepeatedValue {
  return { repeated: values };
}

/**
 * Build a request path with a query string, dropping params that carry no value.
 * Skipping them rather than sending blanks matters: the backend's Zod schemas
 * apply their defaults on absent params but reject empty strings.
 */
export function apiPath(path: string, params: QueryParams = {}): string {
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(","));
    } else if (typeof value === "object") {
      for (const entry of value.repeated) search.append(key, entry);
    } else {
      search.set(key, String(value));
    }
  }

  const query = search.toString();
  return query ? `${suffix}?${query}` : suffix;
}

/** A non-2xx response from the backend. */
export class ApiError extends Error {
  constructor(readonly status: number, readonly path: string) {
    super(`Backend responded ${status} for ${path}`);
    this.name = "ApiError";
  }
}

/**
 * Browser-side fetch to the backend.
 *
 * The path is relative on purpose: the client is already on the tenant's own
 * origin, where Traefik routes `/api/*` to the backend. `BACKEND_ORIGIN` is
 * server-only (deliberately not `NEXT_PUBLIC_`) and has no meaning here.
 *
 * Unlike `backendFetch`, this *throws* on a non-2xx rather than returning null:
 * server components degrade gracefully into a fallback render, while client
 * callers go through react-query, which needs a rejection to enter its error
 * state instead of caching an empty success.
 */
export async function apiFetch<T>(
  path: string,
  opts: { params?: QueryParams; lang?: string; signal?: AbortSignal } = {}
): Promise<T> {
  const withLang = opts.lang
    ? { ...opts.params, lang: opts.lang }
    : opts.params;
  const url = `/api${apiPath(path, withLang)}`;

  const res = await fetch(url, {
    signal: opts.signal,
    // Same-origin (Traefik proxies /api on the tenant's own host), so the
    // first-party session cookie is sent; explicit for a protected tenant.
    credentials: "same-origin",
    headers: opts.lang ? { "accept-language": opts.lang } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, url);
  return (await res.json()) as T;
}

/**
 * Browser-side POST of a JSON body to the backend.
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  opts: { signal?: AbortSignal } = {}
): Promise<T> {
  const url = `/api${apiPath(path)}`;

  const res = await fetch(url, {
    method: "POST",
    signal: opts.signal,
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, url);
  return (await res.json()) as T;
}
